<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useSlots,
  watch,
} from "vue";
import { TzjPlayerProps, uuid } from "./tzj-player";

defineOptions({
  name: "EasyPlayer",
});

const props = defineProps(TzjPlayerProps);
const emits = defineEmits([
  "timeout",
  "error",
  "play",
  "pause",
  "mute",
  "videoInfo",
]);
const slots = useSlots();
const customBar = computed(() => slots.customBar);

let player: any = null;
const id = uuid();
const playerRef = shallowRef<HTMLDivElement>();
const playSuccess = ref(true);
const playUrl = ref("");
const videoInfo = ref<any>(null);
const eventHandlers = ref<Map<string, Function>>(new Map());
// 用于追踪等待 EasyPlayerPro 加载的轮询定时器，防止组件卸载后泄漏
let waitTimer: ReturnType<typeof setInterval> | null = null;
// 用于定时重启播放器的定时器，防止播放器卡死
const restartTimer = ref<ReturnType<typeof setInterval> | null>(null);
// 用于定时暂停播放器的定时器（pauseMode=3时，每60分钟触发一次暂停）
const pauseCycleTimer = ref<ReturnType<typeof setInterval> | null>(null);
// ResizeObserver：监听容器尺寸变化，同步更新播放器 DOM 尺寸
let resizeObserver: ResizeObserver | null = null;
// startPlayer 重试计数，处理 worklet/AbortError 偶发失败
let createRetryCount = 0;
const MAX_CREATE_RETRY = 3;
// 标记组件是否已卸载，防止卸载后的异步操作继续执行
let isUnmounted = false;

onMounted(() => {
  // 监听容器尺寸变化，同步播放器 DOM 尺寸（防止控件丢失）
  if (typeof ResizeObserver !== "undefined" && playerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        const dom = document.getElementById(id);
        if (dom && width > 0 && height > 0) {
          dom.style.width = `${width}px`;
          dom.style.height = `${height}px`;
        }
      }
    });
    resizeObserver.observe(playerRef.value);
  }
  initPlayer(props.url || "");
});

watch(
  () => props.url,
  (newUrl: string) => {
    initPlayer(newUrl || "");
  },
);

onBeforeUnmount(() => {
  isUnmounted = true;
  // 清除 ResizeObserver
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  // 清除等待 EasyPlayerPro 加载的轮询定时器，防止卸载后继续创建播放器
  if (waitTimer !== null) {
    clearInterval(waitTimer);
    waitTimer = null;
  }
  // 清除定时暂停的定时器
  if (pauseCycleTimer.value !== null) {
    clearInterval(pauseCycleTimer.value);
    pauseCycleTimer.value = null;
  }
  if (props.debug) {
    console.log("组件卸载");
  }
  playUrl.value = "";
  destroy();
});

const play = (url?: string) => {
  if (url) {
    initPlayer(url);
  } else {
    if (player) {
      player.play(playUrl.value);
      // pauseMode=3 时，继续播放后重新开始60分钟计时
      if (props.pauseMode === 3) {
        if (pauseCycleTimer.value !== null) {
          clearInterval(pauseCycleTimer.value);
        }
        pauseCycleTimer.value = setInterval(
          () => {
            if (!isUnmounted && player) {
              player.pause?.();
              if (props.debug) {
                console.log("pauseMode: 每60分钟定时暂停");
              }
            }
          },
          1000 * 60 * 10,
        );
      }
    } else {
      destroy();
    }
  }
};

const playerPause = () => {
  if (props.debug) {
    console.log("暂停");
  }
  player?.pause?.();
};

const playerScreenShot = () => {
  if (props.debug) {
    console.log("截图");
  }
  player?.screenshot?.(
    `${props.areaName}_${new Date().getTime()}`,
    "png",
    0.5,
    "download",
  );
};

defineExpose({
  play,
  destroy,
  getPlayer: () => player,
  pause: playerPause,
  screenshot: playerScreenShot,
  isMute: () => {
    return player?.isMute?.();
  },
  setMute: (mute: number) => {
    player?.setMute?.(mute);
  },
});

/**
 * 格式化URL，确保使用完整的URL
 * @param url - 原始URL
 * @returns 格式化后的URL
 */
const formatUrl = (url: string): string => {
  if (!url) return "";

  // 如果已经是完整URL，直接返回
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("ws://")
  ) {
    return url;
  }

  // 如果URL以//开头，自动补全协议（根据当前页面协议）
  if (url.startsWith("//")) {
    if (typeof window !== "undefined" && window.location) {
      return `${window.location.protocol}${url}`;
    }
    // SSR环境下默认使用https
    return `https:${url}`;
  }

  // 检查是否在浏览器环境中，避免SSR环境下的location访问错误
  if (typeof window !== "undefined" && window.location) {
    // 如果是相对路径，添加当前域名
    return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
  }

  // SSR 环境下返回相对路径
  return url;
};

async function initPlayer(url: string) {
  if (props.debug) {
    console.log("初始化播放器", url);
  }
  // 清除上一次可能残留的轮询定时器
  if (waitTimer !== null) {
    clearInterval(waitTimer);
    waitTimer = null;
  }
  removePlayerDom();
  if (url) {
    playUrl.value = formatUrl(url);
    if (props.debug) {
      console.log("格式化后的url", playUrl.value);
    }
    if (typeof window !== "undefined" && window?.EasyPlayerPro) {
      createPlayer();
    } else if (typeof window !== "undefined") {
      waitTimer = setInterval(() => {
        if (window?.EasyPlayerPro) {
          clearInterval(waitTimer!);
          waitTimer = null;
          // 组件已卸载则不再创建播放器
          if (!isUnmounted) {
            createPlayer();
          }
        }
      }, 100);
    }
  }
}

/**
 * 移除播放器DOM
 */
function removePlayerDom() {
  if (typeof document !== "undefined") {
    const dom = document.getElementById(id);
    dom && dom.remove();
  }
}

/**
 * 创建播放器DOM
 */
function createPlayerDom(): Promise<HTMLDivElement> {
  removePlayerDom();
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(null!);
      return;
    }
    // 最大重试帧数，防止无限等待
    let retryCount = 0;
    const MAX_RETRY = 60; // 约 1 秒 (60 * 16ms)

    const tryCreate = () => {
      const rect = playerRef.value?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) {
        if (++retryCount < MAX_RETRY) {
          requestAnimationFrame(tryCreate);
          return;
        }
        // 超过最大重试次数，使用 100% 尺寸兜底
        console.warn("[EasyPlayer] 容器尺寸获取超时，使用默认尺寸");
      }

      const dom = document.createElement("div");
      dom.id = id;
      // 使用实际尺寸，若获取失败则用 100%
      const rectW = rect?.width ?? 0;
      const rectH = rect?.height ?? 0;
      console.log("🚀 ~ rectH:", rectH);
      const w = rectW > 0 ? rectW : playerRef.value?.offsetWidth || 800;
      const h = rectH > 0 ? rectH : playerRef.value?.offsetHeight || 450;

      dom.style.width = `${w}px`;
      dom.style.height =
        props.areaName || props.deviceSerial ? `${h - 32}px` : `${h}px`;
      playerRef.value?.appendChild(dom);
      resolve(dom);
    };

    requestAnimationFrame(tryCreate);
  });
}

async function createPlayer() {
  if (props.debug) {
    console.log("创建播放器");
  }
  videoInfo.value = null;
  await createPlayerDom();
  // 每30秒重启一次播放器，防止播放器卡死
  if (restartTimer.value !== null) {
    clearInterval(restartTimer.value);
    restartTimer.value = null;
  }
  restartTimer.value = setInterval(
    () => {
      destroy(false);
      setTimeout(() => {
        startPlayer();
      }, 1000 * 20);
    },
    1000 * 60 * 60,
  );
  startPlayer();
}

async function startPlayer() {
  try {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const isMicro = window.__MICRO_APP_ENVIRONMENT__;
      console.log("🚀 ~ isMicro:", isMicro);
      player = new window.EasyPlayerPro(document.getElementById(id), {
        isLive: true, // 是否直播
        hasAudio: false, // 是否解析音频
        bufferTime: 0.2, // 直播监控缓存时长设为0，减少内存堆积
        stretch: true, // 加视频拉伸
        MSE: true, // H.264 走 MSE 硬解
        WCS: true, // H.265 走 WebCodecs 硬解（Chrome 的 MSE 不支持 H265，需靠 WCS 兜底）
        WASM: true, // 硬解不可用时降级 WASM 软解
        debug: true,
        maxDelay: 300, // 最大容忍延迟300ms，超时自动丢帧追实时画面
        reconnect: true, // 断线自动重连，单路故障不影响其他8路
        // // 使用动态拼接的绝对路径
        // decoderPath: `${window.location.origin}/plugins/easy-player/`,
        // decoder: "EasyPlayer-decode.js",
      });
      // 创建成功，重置重试计数
      createRetryCount = 0;
    }
    if (player) {
      player.play(playUrl.value);

      // 根据 pauseMode 处理暂停逻辑
      // pauseMode=2 或 3 时，初始默认暂停
      if (props.pauseMode === 2 || props.pauseMode === 3) {
        // 短暂延迟后暂停，确保播放器已开始播放再暂停
        setTimeout(() => {
          if (!isUnmounted && player) {
            player.pause?.();
            if (props.debug) {
              console.log("pauseMode: 初始默认暂停");
            }
          }
        }, 500);
      }

      // pauseMode=3 时，每60分钟触发一次暂停
      if (props.pauseMode === 3) {
        if (pauseCycleTimer.value !== null) {
          clearInterval(pauseCycleTimer.value);
        }
        pauseCycleTimer.value = setInterval(
          () => {
            if (!isUnmounted && player) {
              player.pause?.();
              if (props.debug) {
                console.log("pauseMode: 每60分钟定时暂停");
              }
            }
          },
          1000 * 60 * 60,
        );
      }

      const timeoutHandler = () => {
        props.debug && console.log("加载超时");
        emits("timeout");
        playSuccess.value = false;
        destroy();
        initPlayer(playUrl.value);
      };

      const errorHandler = (error?: any) => {
        props.debug && console.log("播放异常", error);
        emits("error", error);
        playSuccess.value = false;
        // destroy();
      };

      const playHandler = () => {
        playSuccess.value = true;
        props.debug && console.log("播放");
        emits("play");
      };

      const pauseHandler = () => {
        props.debug && console.log("暂停");
        emits("pause");
      };

      const videoInfoHandler = (info: any) => {
        props.debug && console.log("视频信息", info);
        videoInfo.value = info;
        emits("videoInfo", info);
      };

      const muteHandler = (mute: false) => {
        props.debug && console.log("静音");
        emits("mute", mute);
      };

      player.on("timeout", timeoutHandler);
      player.on("error", errorHandler);
      player.on("play", playHandler);
      player.on("pause", pauseHandler);
      player.on("videoInfo", videoInfoHandler);
      player.on("mute", muteHandler);

      eventHandlers.value.set("timeout", timeoutHandler);
      eventHandlers.value.set("error", errorHandler);
      eventHandlers.value.set("play", playHandler);
      eventHandlers.value.set("pause", pauseHandler);
      eventHandlers.value.set("videoInfo", videoInfoHandler);
      eventHandlers.value.set("mute", muteHandler);
    }
  } catch (error) {
    const isWorkletError =
      error instanceof DOMException &&
      (error.name === "AbortError" ||
        String(error.message).includes("worklet"));
    // worklet 加载偶发失败，自动重试
    if (isWorkletError && createRetryCount < MAX_CREATE_RETRY) {
      createRetryCount++;
      if (props.debug) {
        console.warn(
          `[EasyPlayer] AudioWorklet 加载失败（第 ${createRetryCount}/${MAX_CREATE_RETRY} 次），1秒后重试...`,
          error,
        );
      }
      destroy(false);
      setTimeout(() => {
        if (!isUnmounted) startPlayer();
      }, 1000);
      return;
    }
    console.error("创建播放器失败:", error);
    setTimeout(() => {
      if (!isUnmounted) startPlayer();
    }, 2000);
  }
}

function destroy(flag = true) {
  if (player) {
    eventHandlers.value.forEach((handler, eventName) => {
      player.off(eventName, handler);
    });
    eventHandlers.value.clear();
    player.destroy();
    player = null;
  }

  if (flag) {
    // 清除定时重启播放器的定时器
    if (restartTimer.value !== null) {
      clearInterval(restartTimer.value);
      restartTimer.value = null;
    }
    // 清除定时暂停的定时器
    if (pauseCycleTimer.value !== null) {
      clearInterval(pauseCycleTimer.value);
      pauseCycleTimer.value = null;
    }
    removePlayerDom();
  }
}
</script>

<template>
  <div
    ref="playerRef"
    class="i-tzj-easyPlayer"
    :class="customBar ? 'i-tzj-easyPlayer--custom' : ''"
  >
    <!-- 播放器底部操作栏 -->
    <slot name="customBar" />
  </div>
</template>
